import {Alert, Button, Form, Row} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  getPlayerProfile,
  reactivatePlayer,
  unlockPlayer,
  resetPlayerPassword,
  setBirthday,
  setEmail,
  setFirstName,
  setLastName,
  setGuestId,
  resendUserVerification,
} from "../actions/backOfficeAction";
import InputMask from "react-input-mask";
import ResetPasswordVerifyOverlay from "./ResetPasswordVerifyOverlay";

const PlayerProfileRegisteredDetails = ({playerProfile}) => {

  const dispatch = useDispatch();

  // reset-password confirmation modal state
  const [showResetVerifyOverlay, setShowResetVerifyOverlay] = useState(false);
  const [resetEmailToConfirm, setResetEmailToConfirm] = useState(null);

  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(playerProfile?.email);
  const [editingFirstName, setEditingFirstName] = useState(false);
  const [newFirstName, setNewFirstName] = useState(playerProfile?.firstName);
  const [editingLastName, setEditingLastName] = useState(false);
  const [newLastName, setNewLastName] = useState(playerProfile?.lastName);
  const [editingGuestId, setEditingGuestId] = useState(false);
  const [newGuestId, setNewGuestId] = useState(playerProfile?.guestId);

  useEffect(() => {
    setNewEmail(playerProfile?.email);
    setNewFirstName(playerProfile?.firstName);
    setNewLastName(playerProfile?.lastName);
    setNewGuestId(playerProfile?.guestId);
  }, [playerProfile]);

  const reactivatePlayerProcessing = useSelector(
    (state) => state.backOfficeState.reactivatePlayerProcessing
  );
  const reactivatePlayerResult = useSelector(
    (state) => state.backOfficeState.reactivatePlayerResult
  );
  const reactivatePlayerError = useSelector(
    (state) => state.backOfficeState.reactivatePlayerError
  );

  // unlock state (separate from reactivate)
  const unlockPlayerProcessing = useSelector(
    (state) => state.backOfficeState.unlockPlayerProcessing
  );
  const unlockPlayerResult = useSelector(
    (state) => state.backOfficeState.unlockPlayerResult
  );
  const unlockPlayerError = useSelector(
    (state) => state.backOfficeState.unlockPlayerError
  );

  // reset password state
  const resetPasswordProcessing = useSelector(
    (state) => state.backOfficeState.resetPasswordProcessing
  );
  const resetPasswordResult = useSelector(
    (state) => state.backOfficeState.resetPasswordResult
  );
  const resetPasswordError = useSelector(
    (state) => state.backOfficeState.resetPasswordError
  );

  // errors from profile actions used in this component
  const setBirthdayError = useSelector((state) => state.backOfficeState.setBirthdayError);
  const setEmailError = useSelector((state) => state.backOfficeState.setEmailError);
  const setFirstNameError = useSelector((state) => state.backOfficeState.setFirstNameError);
  const setLastNameError = useSelector((state) => state.backOfficeState.setLastNameError);
  const setGuestIdError = useSelector((state) => state.backOfficeState.setGuestIdError);

  // helper to convert various error shapes to readable strings
  const formatError = (err) => {
     if (!err) return null;
     if (typeof err === "string") return err;
     if (Array.isArray(err)) return err.map((e) => (typeof e === "string" ? e : JSON.stringify(e))).join("; ");
     if (err.message) return err.message;
     try {
       return JSON.stringify(err);
     } catch (e) {
       return String(err);
     }
  };

  const combinedErrors = [
    reactivatePlayerError,
    unlockPlayerError,
    resetPasswordError,
    setBirthdayError,
    setEmailError,
    setFirstNameError,
    setLastNameError,
    setGuestIdError,
  ].map(formatError).filter(Boolean);

  async function reactivateClickedHandler(guestId) {
    await dispatch(reactivatePlayer(guestId, playerProfile.username));
    dispatch(getPlayerProfile(guestId));
  }

  async function unlockClickedHandler(guestId) {
    await dispatch(unlockPlayer(guestId, playerProfile.username));
    dispatch(getPlayerProfile(guestId));
  }

  async function resetPasswordClickedHandler(email) {
    try {
      await dispatch(resetPlayerPassword(email));
      // refresh profile after reset action
      dispatch(getPlayerProfile(playerProfile?.guestId));
    } catch (e) {
      // noop - toast handled in thunk
    }
  }

  async function resendVerificationClickedHandler(email) {
    try {
      await dispatch(resendUserVerification(email));
      // optional: refresh profile after resend if backend changes state
      dispatch(getPlayerProfile(playerProfile?.guestId));
    } catch (e) {
      // noop - toast shown by thunk
    }
  }

  // Open confirmation modal instead of directly calling reset
  function openResetVerifyOverlay(email) {
    setResetEmailToConfirm(email);
    setShowResetVerifyOverlay(true);
  }

  function closeResetVerifyOverlay() {
    setShowResetVerifyOverlay(false);
    setResetEmailToConfirm(null);
  }

  async function proceedResetFromOverlay() {
    setShowResetVerifyOverlay(false);
    try {
      await resetPasswordClickedHandler(resetEmailToConfirm);
    } finally {
      setResetEmailToConfirm(null);
    }
  }

  function editEmailClickedHandler() {
    setNewEmail(playerProfile?.username ?? '');
    setEditingEmail(true);
  }
  async function saveEmailHandler() {
    try {
      await dispatch(setEmail(playerProfile?.guestId, newEmail));
      dispatch(getPlayerProfile(playerProfile?.guestId));
    } finally {
      setEditingEmail(false);
    }
  }
  function cancelEmailHandler() { setEditingEmail(false); }

  function editFirstNameClickedHandler() {
    setNewFirstName(playerProfile?.firstName ?? '');
    setEditingFirstName(true);
  }
  async function saveFirstNameHandler() {
    try {
      await dispatch(setFirstName(playerProfile?.guestId, newFirstName));
      dispatch(getPlayerProfile(playerProfile?.guestId));
    } finally {
      setEditingFirstName(false);
    }
  }
  function cancelFirstNameHandler() { setEditingFirstName(false); }

  function editLastNameClickedHandler() {
    setNewLastName(playerProfile?.lastName ?? '');
    setEditingLastName(true);
  }
  async function saveLastNameHandler() {
    try {
      await dispatch(setLastName(playerProfile?.guestId, newLastName));
      dispatch(getPlayerProfile(playerProfile?.guestId));
    } finally {
      setEditingLastName(false);
    }
  }
  function cancelLastNameHandler() { setEditingLastName(false); }

  function editGuestIdClickedHandler() {
    setNewGuestId(playerProfile?.guestId ?? '');
    setEditingGuestId(true);
  }
  async function saveGuestIdHandler() {
    try {
      await dispatch(setGuestId(playerProfile?.username, newGuestId));
      dispatch(getPlayerProfile(newGuestId || playerProfile?.guestId));
    } finally {
      setEditingGuestId(false);
    }
  }
  function cancelGuestIdHandler() { setEditingGuestId(false); }

  return (
    <div className="admin-screen-divider-div player-profile-details">
      <h4 className="player-profile-details-header">
        <span className={playerProfile?.verified ? "" : "player-unverified-label"}>
          {playerProfile?.verified ? "Registered User" : "New user who has not yet verified their email"}
        </span>
        <br />
        {playerProfile?.firstName} {playerProfile?.lastName}, Player ID: {playerProfile?.guestId}
      </h4>
      <div className="player-profile-details">

        {/* Email edit-in-place */}
        <div className="edit-in-place-form-field">
          {editingEmail ? (
            <>
              <Form.Group as={Row} controlId="editEmail">
                <Form.Label className="gcotg-form-label">Email</Form.Label>
                <Form.Control
                  placeholder="Email"
                  value={newEmail || ''}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>
              <Button variant="primary" size="sm" className="in-place-form-button-secondary"
                      onClick={cancelEmailHandler}>Cancel</Button>
              <Button variant="primary" size="sm" className="in-place-form-button-primary"
                      onClick={saveEmailHandler}>Save New Email</Button>
            </>
          ) : (
            <span className="player-profile-details-non-edit-container">
              <span>Email: {playerProfile?.username}</span>
              <Button variant="primary" size="sm" className="gcotg-button"
                      onClick={editEmailClickedHandler}>Edit Email</Button>
            </span>
          )}
        </div>

        {/* First name edit-in-place */}
        <div className="edit-in-place-form-field">
          {editingFirstName ? (
            <>
              <Form.Group as={Row} controlId="editFirstName">
                <Form.Label className="gcotg-form-label">First Name</Form.Label>
                <Form.Control
                  placeholder="First Name"
                  value={newFirstName || ''}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>
              <Button variant="primary" size="sm" className="in-place-form-button-secondary"
                      onClick={cancelFirstNameHandler}>Cancel</Button>
              <Button variant="primary" size="sm" className="in-place-form-button-primary"
                      onClick={saveFirstNameHandler}>Save New First Name</Button>
            </>
          ) : (
            <span className="player-profile-details-non-edit-container">
              <span>First Name: {playerProfile?.firstName}</span>
              <Button variant="primary" size="sm" className="gcotg-button"
                      onClick={editFirstNameClickedHandler}>Edit First Name</Button>
            </span>
          )}
        </div>

        {/* Last name edit-in-place */}
        <div className="edit-in-place-form-field">
          {editingLastName ? (
            <>
              <Form.Group as={Row} controlId="editLastName">
                <Form.Label className="gcotg-form-label">Last Name</Form.Label>
                <Form.Control
                  placeholder="Last Name"
                  value={newLastName || ''}
                  onChange={(e) => setNewLastName(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>
              <Button variant="primary" size="sm" className="in-place-form-button-secondary"
                      onClick={cancelLastNameHandler}>Cancel</Button>
              <Button variant="primary" size="sm" className="in-place-form-button-primary"
                      onClick={saveLastNameHandler}>Save New Last Name</Button>
            </>
          ) : (
            <span className="player-profile-details-non-edit-container">
              <span>Last Name: {playerProfile?.lastName}</span>
              <Button variant="primary" size="sm" className="gcotg-button"
                      onClick={editLastNameClickedHandler}>Edit Last Name</Button>
            </span>
          )}
        </div>

        {/* Guest ID edit-in-place */}
        <div className="edit-in-place-form-field">
          {editingGuestId ? (
            <>
              <Form.Group as={Row} controlId="editGuestId">
                <Form.Label className="gcotg-form-label">Guest ID</Form.Label>
                <Form.Control
                  placeholder="Rewards ID"
                  value={newGuestId || ''}
                  onChange={(e) => setNewGuestId(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>
              <Button variant="primary" size="sm" className="in-place-form-button-secondary"
                      onClick={cancelGuestIdHandler}>Cancel</Button>
              <Button variant="primary" size="sm" className="in-place-form-button-primary"
                      onClick={saveGuestIdHandler}>Save New Rewards ID</Button>
            </>
          ) : (
            <span className="player-profile-details-non-edit-container">
              <span>Rewards ID: {playerProfile?.guestId}</span>
              <Button variant="primary" size="sm" className="gcotg-button"
                      onClick={editGuestIdClickedHandler}>Edit Rewards ID</Button>
            </span>
          )}
        </div>

        {playerProfile?.disabled && playerProfile?.verified &&
          <Alert variant='warning'>
            {playerProfile?.disabled ? "LOGIN DISABLED" : ""}
            <span className="reactivate-button-span">
                <Button variant="primary" size="sm" className="gcotg-button"
                        onClick={() => reactivateClickedHandler(playerProfile?.guestId)}
                        disabled={reactivatePlayerProcessing ? true : undefined}>
                Reactivate
                </Button>
              </span>
          </Alert>
        }

        {playerProfile?.locked && playerProfile?.verified &&
          <Alert variant='warning'>
            {playerProfile?.locked ? "ACCOUNT LOCKED" : ""}
            <span className="reactivate-button-span">
                <Button variant="primary" size="sm" className="gcotg-button"
                        onClick={() => unlockClickedHandler(playerProfile?.guestId)}
                        disabled={unlockPlayerProcessing ? true : undefined}>
                Unlock
                </Button>
              </span>
          </Alert>
        }

        {playerProfile?.username && playerProfile?.verified &&
          <div style={{marginTop:10}}>
              <Button variant="primary" size="sm" className="gcotg-button"
                    onClick={() => openResetVerifyOverlay(playerProfile?.username)}
                    disabled={resetPasswordProcessing ? true : undefined}>
                Reset Password
              </Button>
          </div>
        }

        {/* Resend verification button - show when user has an email but is not yet verified */}
        {playerProfile?.username && !playerProfile?.verified &&
          <div style={{marginTop:10}}>
              <Button variant="primary" size="sm" className="gcotg-button"
                    onClick={() => resendVerificationClickedHandler(playerProfile?.username)}
                    disabled={resetPasswordProcessing ? true : undefined}>
                Resend Verification Email
              </Button>
          </div>
        }

        {/* Reset password confirmation modal */}
        <ResetPasswordVerifyOverlay
          show={showResetVerifyOverlay}
          email={resetEmailToConfirm}
          handleClose={closeResetVerifyOverlay}
          handleProceed={proceedResetFromOverlay}
        />

        {/* Combined server error messages for actions in this component */}
        {combinedErrors.length > 0 && (
          <Alert variant="danger" className="gcotg-error-message" style={{marginTop:12}}>
            {combinedErrors.map((errMsg, idx) => (
              <div key={idx}>{errMsg}</div>
            ))}
          </Alert>
        )}

      </div>
    </div>
  );
}

export default PlayerProfileRegisteredDetails;
